import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';
import { useTheme } from '../contexts/ThemeContext';
import { getClassNames } from '../styles/theme';
import { ArrowRight, Sparkles, BookOpen, Hammer, Compass, Wallet } from 'lucide-react';
import logoHero from '../assets/logos/LL(512 x 512 px).png';

const LandingPage: React.FC = () => {
  const { isConnected, connectWallet, isLoading } = useWallet();
  const { theme } = useTheme();
  const classNames = getClassNames(theme);
  
  // Debug logging
  console.log('LandingPage theme:', theme);
  console.log('LandingPage background class:', classNames.background);

  return (
    <div className={`min-h-screen ${classNames.background}`}>
      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-accent-wood to-accent-wood-light text-white py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-2 text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            <span>Secure your legacy as you build it</span>
            <Sparkles className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Main Logo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex justify-center mb-8"
            >
              <Link to="/" className="cursor-pointer hover:scale-105 transition-transform">
                <img src={logoHero} alt="LegacyLayer" className="w-64 h-64 md:w-80 md:h-80 object-contain" />
              </Link>
            </motion.div>



            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className={`text-xl md:text-2xl ${classNames.textSecondary} mb-12 font-light max-w-3xl mx-auto`}
            >
              Tokenize and document the things you love and value. 
              Build provenance through their journey and share precious memories with your family.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
            >
              {!isConnected ? (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={connectWallet}
                  disabled={isLoading}
                  className={classNames.buttonPrimary}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <Wallet className="w-5 h-5" />
                      Connect Wallet
                    </>
                  )}
                </motion.button>
              ) : (
                <Link
                  to="/build"
                  className={classNames.buttonPrimary}
                >
                  <Hammer className="w-5 h-5" />
                  Start Building
                  <ArrowRight className="w-5 h-5" />
                </Link>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className={`py-20 ${theme === 'dark' ? 'bg-primary-800' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Learn */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className={classNames.cardPrimary}
            >
              <div className={classNames.icon.large}>
                <BookOpen className="w-8 h-8 text-accent-wood" />
              </div>
              <h3 className={`text-xl font-semibold ${classNames.textPrimary} mb-2`}>Learn</h3>
              <p className={classNames.textSecondary}>Discover how to preserve your family's legacy through blockchain technology</p>
            </motion.div>

            {/* Build */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className={classNames.cardPrimary}
            >
              <div className={classNames.icon.large}>
                <Hammer className="w-8 h-8 text-accent-wood" />
              </div>
              <h3 className={`text-xl font-semibold ${classNames.textPrimary} mb-2`}>Build</h3>
              <p className={classNames.textSecondary}>Create and tokenize your precious items with our intuitive tools</p>
            </motion.div>

            {/* Explore */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={classNames.cardPrimary}
            >
              <div className={classNames.icon.large}>
                <Compass className="w-8 h-8 text-accent-wood" />
              </div>
              <h3 className={`text-xl font-semibold ${classNames.textPrimary} mb-2`}>Explore</h3>
              <p className={classNames.textSecondary}>Browse and discover legacy items from around the world</p>
            </motion.div>

            {/* Wallet */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className={classNames.cardPrimary}
            >
              <div className={classNames.icon.large}>
                <Wallet className="w-8 h-8 text-accent-wood" />
              </div>
              <h3 className={`text-xl font-semibold ${classNames.textPrimary} mb-2`}>Wallet</h3>
              <p className={classNames.textSecondary}>Manage your digital assets and family legacy securely</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content Containers */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Container 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-lg border border-primary-200 p-8 mb-8"
          >
            <h2 className="text-3xl font-display font-bold text-primary-900 mb-4">
              Preserve Your <span className="text-accent-wood">Legacy</span>
            </h2>
            <p className="text-primary-600 text-lg">
              This is where we'll showcase the core value proposition and key features of LegacyLayer.
              Content coming soon...
            </p>
          </motion.div>

          {/* Container 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-br from-accent-wood/5 to-accent-wood/10 rounded-2xl border border-accent-wood/20 p-8 mb-8"
          >
            <h2 className="text-3xl font-display font-bold text-primary-900 mb-4">
              Build <span className="text-accent-wood">Provenance</span>
            </h2>
            <p className="text-primary-600 text-lg">
              This section will highlight how users can build and track the provenance of their valuable items.
              Content coming soon...
            </p>
          </motion.div>

          {/* Container 3 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg border border-primary-200 p-8"
          >
            <h2 className="text-3xl font-display font-bold text-primary-900 mb-4">
              Share with <span className="text-accent-wood">Family</span>
            </h2>
            <p className="text-primary-600 text-lg">
              This section will explain how families can share and preserve their legacy together.
              Content coming soon...
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage; 